/**
 * Backoffice CRM: a message's text in an inbox thread, as light Markdown (#1695).
 *
 * Emails, reviews and in-app feedback arrive as plain text (`body_text`, `body_trimmed`), often written with Markdown
 * habits: **bold**, bullet lines, links, `>` quotes. This draws a small subset of it — paragraphs, line breaks, bold,
 * italics, inline code, links and bare URLs, bulleted and numbered lists, quotes and headings — and nothing else.
 * Everything the sender wrote is escaped before any tag is added, and links only go to http(s) and mailto, so no
 * markup or script in a message reaches the page. The email's own HTML stays behind "Show original".
 */
import { escapeHTML } from "./familyDetailsPanel.js";

const LINK = "font-medium text-paprika-700 underline hover:text-paprika-600 dark:text-paprika-400";
const CODE = "rounded bg-gray-100 px-1 font-mono text-[0.85em] dark:bg-white/10";

/** Above either, a message may be too tall for its bubble: the page measures it and offers Show more (#1695). */
const LONG_CHARS = 500;
const LONG_LINES = 10;

/** Whether a message's text is long enough to be drawn collapsed until the page has measured it. */
export function isLongMessage(text) {
  const s = String(text || "");
  return s.length > LONG_CHARS || s.split("\n").length > LONG_LINES;
}

/** The message's text as HTML: escaped, then the Markdown subset above. */
export function messageBodyHTML(text) {
  return blocksHTML(String(text || "").replace(/\r\n?/g, "\n").replace(/\u0000/g, "").split("\n"));
}

function blocksHTML(lines) {
  const out = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    if (!line.trim()) { i += 1; continue; }
    if (/^\s*>/.test(line)) {
      const quoted = [];
      while (i < lines.length && /^\s*>/.test(lines[i])) quoted.push(lines[i++].replace(/^\s*> ?/, ""));
      out.push(`<blockquote class="space-y-2 border-l-2 border-gray-300 pl-3 text-gray-600 dark:border-white/20 dark:text-gray-400">${blocksHTML(quoted)}</blockquote>`);
      continue;
    }
    const heading = /^\s{0,3}#{1,6}\s+(.+?)\s*#*\s*$/.exec(line);
    if (heading) {
      out.push(`<p class="font-semibold">${inlineHTML(heading[1])}</p>`);
      i += 1;
      continue;
    }
    const list = listKind(line);
    if (list) {
      const items = [];
      while (i < lines.length && listKind(lines[i]) === list) {
        items.push(inlineHTML(lines[i].replace(list === "ol" ? /^\s*\d+[.)]\s+/ : /^\s*[-*•]\s+/, "")));
        i += 1;
        // A wrapped item: the next line, indented and not an item of its own, continues it.
        while (i < lines.length && /^\s{2,}\S/.test(lines[i]) && !listKind(lines[i])) items[items.length - 1] += `<br>${inlineHTML(lines[i++].trim())}`;
      }
      out.push(list === "ol"
        ? `<ol class="list-decimal space-y-1 pl-5">${items.map((h) => `<li>${h}</li>`).join("")}</ol>`
        : `<ul class="list-disc space-y-1 pl-5">${items.map((h) => `<li>${h}</li>`).join("")}</ul>`);
      continue;
    }
    const para = [];
    while (i < lines.length && lines[i].trim() && !/^\s*>/.test(lines[i]) && !listKind(lines[i]) && !/^\s{0,3}#{1,6}\s/.test(lines[i])) para.push(inlineHTML(lines[i++]));
    out.push(`<p>${para.join("<br>")}</p>`);
  }
  return out.join("");
}

function listKind(line) {
  if (/^\s*[-*•]\s+\S/.test(line)) return "ul";
  if (/^\s*\d+[.)]\s+\S/.test(line)) return "ol";
  return null;
}

/** Code spans and links are set aside first, so neither is escaped twice or formatted inside. */
const TOKENS = /`([^`\n]+)`|\[([^\]\n]+)\]\(((?:https?:\/\/|mailto:)[^\s)]+)\)|((?:https?:\/\/|www\.)[^\s<>"]+)/g;
const TRAILING = /[.,;:!?'")\]]+$/;

function inlineHTML(raw) {
  const tokens = [];
  const keep = (html) => `\u0000${tokens.push(html) - 1}\u0000`;
  let s = String(raw).replace(TOKENS, (match, code, label, url, bare) => {
    if (code !== undefined) return keep(`<code class="${CODE}">${escapeHTML(code)}</code>`);
    if (label !== undefined) return keep(linkHTML(url, escapeHTML(label)));
    const tail = (bare.match(TRAILING) || [""])[0];
    const address = bare.slice(0, bare.length - tail.length);
    if (!address || address === "www.") return match;
    return keep(linkHTML(address.startsWith("www.") ? `https://${address}` : address, escapeHTML(address))) + escapeHTML(tail);
  });
  s = escapeHTML(s)
    .replace(/\*\*(?=\S)(.+?)(?<=\S)\*\*/g, "<strong>$1</strong>")
    .replace(/(^|[^\w])__(?=\S)(.+?)(?<=\S)__(?!\w)/g, "$1<strong>$2</strong>")
    .replace(/(^|[^\w*])\*(?=[^\s*])(.+?)(?<=[^\s*])\*(?![\w*])/g, "$1<em>$2</em>")
    .replace(/(^|[^\w])_(?=[^\s_])(.+?)(?<=[^\s_])_(?!\w)/g, "$1<em>$2</em>");
  return s.replace(/\u0000(\d+)\u0000/g, (_, n) => tokens[Number(n)]);
}

function linkHTML(href, labelHTML) {
  return `<a href="${escapeHTML(href)}" target="_blank" rel="noopener noreferrer nofollow" class="${LINK}">${labelHTML}</a>`;
}
